/**
 * @Description 移动端交互类
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 * @todo 1、移动端所有的交互适配 2、listener处理hud变化 3、改写此函数与cursor相似，以达到每时每刻都在监听硬件事件，但处不处理累加值则由update方法决定。
 */

import * as THREE from 'three'
import { Model } from './Model'
import { Renderer } from './Renderer'
import { HotpointSet } from './Hotpoint'
import { Listener } from './Listener'
import { ScreenInput } from './Input/ScreenInput'
import { CPointSet } from './CameraPoints'

class MobileTouch extends ScreenInput {
  /**
   * 构造函数
   * @param {Element} eventElement 监听事件的元素
   * @param {CPointSet} cps 点位集合
   * @param {Model} model 模型封装
   * @param {HotpointSet} hps 热点列表
   * @param {Renderer} renderer 热点列表
   */
  constructor (eventElement, cps, model, hps, renderer) {
    super(cps)

    /**
     * @type {number} 触摸到的x轴坐标
     */
    this.touchX = 0
    /**
     * @type {number} 触摸到的y轴坐标
     */
    this.touchY = 0
    /**
     * @type {THREE.Vector2} 点1位置
     */
    this.p1 = new THREE.Vector2(0, 0)
    /**
     * @type {THREE.Vector2} 点2位置
     */
    this.p2 = new THREE.Vector2(0, 0)
    /**
     * @type {number} 两点间距离
     */
    this.distance = 0
    /**
     * @type {number} 中点位置
     */
    this.pMid = new THREE.Vector2(0, 0)
    /**
     * @type {number} canvas相对于网页的左侧总偏移量
     */
    this.offsetX = 0
    /**
     * @type {number} canvas相对于网页的上侧总偏移量
     */
    this.offsetY = 0

    /**
     * @type {boolean} 拖拽标志判断
     */
    this.moveFlag = false
    /**
     * @type {boolean} 中断阻尼动画标志
     */
    this.touchDown = false
    /**
     * @type {boolean} 手指松开事件触发标志
     */
    this.touchUp = false
    /**
     * @type {boolean} 退回模型视角标志
     */
    this.back2Model = false

    /**
     * @type {number} damping更改的经度
     */
    this.dampingDeltaLon = 0
    /**
     * @type {number} damping更改的纬度
     */
    this.dampingDeltaLat = 0

    /**
     * @type {Listener} 隐藏hud样式（透明度）的函数列表
     */
    this.hideHUDListener = new Listener()
    /**
     * @type {Listener} 浮现hud样式（透明度）的函数列表
     */
    this.showHUDListener = new Listener()

    /**
     * @type {{panoZooming:boolean,panoRotate:boolean,godRotate:boolean,godPanning:boolean,godZooming:boolean}} 操作类型
     */
    this.operation = {
      panoZooming: false,
      panoRotate: false,
      godRotate: false,
      godPanning: false,
      godZooming: false
    }
    /**
     * @type {number} 总位移X
     */
    this.totalX = 0
    /**
     * @type {number} 总位移Y
     */
    this.totalY = 0
    /**
     * @type {number} 总变化比例
     */
    this.totalRatio = 0
    /**
     * @type {number} 双指中点总变化X
     */
    this.totalMidX = 0
    /**
     * @type {number} 双指中点总变化Y
     */
    this.totalMidY = 0

    /**
     * @type {THREE.Object3D} 当前指向的热点
     */
    this.currHotpoint = null
    /**
     * @type {THREE.Object3D} 当前指向的编辑点
     */
    this.currEditPoint = null
    /**
     * @type {{hotpoint:Symbol,free:Symbol,None:Symbol}} 状态枚举
     */
    this.enumState = Object.freeze({
      hotpoint: Symbol('hotpoit'),
      free: Symbol('free'),
      None: Symbol('None')
    })
    /**
     * @type {symbol} 光标状态
     */
    this.state = this.enumState.free

    // touchcancel 比如电话接入打断当前的touch事件，则触发touchcancel事件

    /**
     * @type {number} 两帧间隔触发
     */
    this.upDelay = 50
    /**
     * @type {boolean} 变化帧
     */
    this.shootMove = false
    /**
     * @type {number} 上一次变化时间戳
     */
    this.lastMoveTimestamp = 0
    /**
     * @type {number} 上一次变化的纬度
     */
    this.pdlat = 0
    /**
     * @type {number} 变化的经度
     */
    this.plon = 0
    /**
     * @type {number} 最后保留几次的变化经纬度
     */
    this.listLength = 5
    /**
     * @type {Array} 纬度变化列表
     */
    this.latList = new Array(this.listLength)
    /**
     * @type {Array} 经度变化列表
     */
    this.lonList = new Array(this.listLength)
    /**
     * @type {number} 列表指针
     */
    this.listPtr = 0

    /**
     * @type {Element} 用于监听事件的dom元素
     */
    this.eventElement = eventElement

    /**
     * @type {Model} 模型封装
     */
    this.model = model

    /**
     * @type {HotpointSet} 热点列表
     */
    this.hps = hps

    /**
     * @type {Renderer} renderer 热点列表
     */
    this.renderer = renderer

    /**
     * @type {function} 移动绑定事件
     */
    this.moveBind = this.onDocumentTouchMove.bind(this)
    /**
     * @type {function} 按下绑定事件
     */
    this.downBind = this.onDocumentTouchStart.bind(this)
    /**
     * @type {function} 弹起绑定事件
     */
    this.upBind = this.onDocumentTouchEnd.bind(this)
  }

  /**
   * 检测触屏是否可用
   * @param {Event} event 触屏事件
   * @returns {boolean} 返回是否可用
   */
  isTouchUsable (event) {
    return true
  }

  /**
   * 初始化添加监听器
   */
  init (setting) {
    this.eventElement.addEventListener('touchstart', this.downBind, { passive: false })
  }

  /**
   * 废弃移除监听
   */
  depose () {
    this.eventElement.removeEventListener('touchstart', this.downBind, { passive: false })
  }

  /**
   * 移动端按下事件处理
   * @param {object} event 移动端按下事件
   */
  onDocumentTouchStart (event) {
    this.renderer.active = true
    event.preventDefault()
    if (this.enable) {
      if (this.cam.cameraMode) {
        this.hideHUDListener.invoke()
      }
      if (event.touches.length === 1) { // 单点触控
        // 移动端没有movement，所以直接用touchX touchY去计算移动的距离
        for (let i = 0; i < this.listLength; ++i) {
          this.lonList[i] = 0
          this.latList[i] = 0
        }

        const touch = event.touches[0]
        this.touchX = touch.pageX
        this.touchY = touch.pageY

        this.moveFlag = false
        this.touchDown = true
        this.counter = 0
        this.eventElement.addEventListener('touchmove', this.moveBind, { passive: false })
        this.eventElement.addEventListener('touchend', this.upBind, { passive: false })
      } else { // 多点触控
        console.log('多点触控事件最开始的地方')
        if (event.touches.length > 2) {
          // 三点及以上 清除一切事件函数
          this.eventElement.removeEventListener('touchend', this.upBind, { passive: false })
          this.eventElement.removeEventListener('touchmove', this.moveBind, { passive: false })
        } else { // 双指情况下 计算出双指中点用于平移与缩放
          this.p1.x = event.touches[0].pageX
          this.p1.y = event.touches[0].pageY
          this.p2.x = event.touches[1].pageX
          this.p2.y = event.touches[1].pageY
          this.distance = this.p1.distanceTo(this.p2)
          this.pMid.x = (this.p1.x + this.p2.x) / 2
          this.pMid.y = (this.p1.y + this.p2.y) / 2
        }
      }
    }
  }

  /**
   * 移动端拖拽事件处理
   * @param {object} event 移动端拖拽移动事件
   */
  onDocumentTouchMove (event) {
    if (!this.isTouchUsable(event)) return
    event.preventDefault()
    const cam = this.cam

    if (this.enable) {
      // 有一些手机在触发down的同时会触发Move，需要排除前几次的影响。
      if (!this.moveFlag && this.counter < 2) {
        this.counter++
        return
      }

      this.moveFlag = true

      if (event.touches.length === 1) { // 单指移动，实现全视角的旋转
        if (this.touchUp) return
        this.oneTouchMove(event, cam)
      } else { // 双指逻辑
        this.twoTouchMove(event, cam)
      }
    }
  }

  /**
   * 单指触屏移动
   * @param {Event} event 事件
   * @param {Camera} cam 相机实例
   */
  oneTouchMove (event, cam) {
    const touch = event.touches[0]

    this.totalX += touch.pageX - this.touchX
    this.totalY += touch.pageY - this.touchY

    if (cam.cameraMode) {
      this.operation.panoRotate = true
    } else { // godMode
      this.operation.godRotate = true
    }
    this.touchX = touch.pageX
    this.touchY = touch.pageY

    this.shootMove = true
    this.lastMoveTimestamp = event.timeStamp
  }

  /**
   * 双指触屏移动
   * @param {Event} event 事件
   * @param {Camera} cam 相机实例
   */
  twoTouchMove (event, cam) {
    const t1 = event.touches[0]
    const t2 = event.touches[1]
    const p1 = new THREE.Vector2(t1.pageX, t1.pageY)
    const p2 = new THREE.Vector2(t2.pageX, t2.pageY)
    const pm = new THREE.Vector2((p1.x + p2.x) / 2, (p1.y + p2.y) / 2)

    const distance = p1.distanceTo(p2)
    this.totalRatio *= distance / this.distance

    if (cam.cameraMode) {
      this.operation.panoZooming = true
      if (cam.fov === 65 && this.totalRatio < 0.8) {
        this.back2Model = true
      }
    } else {
      this.totalMidX += pm.x - this.pMid.x
      this.totalMidY += pm.y - this.pMid.y
      this.operation.godPanning = true
      this.operation.godZooming = true
    }

    this.pMid = pm
    this.distance = distance
  }

  /**
   * 移动端接触点抬起事件处理
   * @param {object} event 移动端触点抬起事件
   */
  onDocumentTouchEnd (event) {
    this.eventElement.removeEventListener('touchmove', this.moveBind, false)
    this.eventElement.removeEventListener('touchend', this.upBind, false)
    if (!this.isTouchUsable(event)) return

    this.showHUDListener.invoke()

    if (this.enable) {
      if (event.timeStamp - this.lastMoveTimestamp < this.upDelay) {
        this.dlat = this.pdlat
        this.dlon = this.pdlon
      }
      this.touchUp = true // 触摸弹起事件
      this.interactUpdate()
      this.handleTouchEvent()
    }
  }

  /**
   * 单指弹起事件处理
   */
  handleSingleTouchUp () {
    this.intersectRls = []

    this.raycaster.intersectObjects(this.model.objects, false, this.intersectRls)
    if (this.intersectRls.length > 0) {
      this.tapPosition.copy(this.intersectRls[0].point)
    }
  }

  /**
   * 更新射线
   */
  updateRaycast () {
    this.width = this.eventElement.clientWidth || this.eventElement.offsetWidth
    this.height = this.eventElement.clientHeight || this.eventElement.offsetHeight
    const touchCoords = new THREE.Vector2()
    touchCoords.x = (this.touchX / this.width) * 2 - 1
    touchCoords.y = -(this.touchY / this.height) * 2 + 1
    this.raycaster.setFromCamera(touchCoords, this.cam.camera)
  }

  /**
   * 状态更新
   */
  stateUpdate () {
    // 检测到hotpoint
    if (this.currHotpoint) {
      this.state = this.enumState.hotpoint
    } else {
      this.state = this.enumState.free
    }
  }

  /**
   * 交互更新
   */
  interactUpdate () {
    if (this.touchUp && !this.moveFlag) {
      this.updateRaycast()
      this.currHotpoint = this.hps.interact(this.raycaster)
      this.stateUpdate()
      if (this.state === this.enumState.free) {
        this.handleSingleTouchUp()
      }
    }
  }

  /**
   * 相机更新
   */
  camUpdate () {
    const cam = this.cam
    // cameraMode
    if (this.operation.panoRotate) {
      this.operation.panoRotate = false
      this.dlon = this.totalX * -0.0015 * cam.fov
      this.dlat = this.totalY * 0.0015 * cam.fov
    }
    if (this.operation.panoZooming) {
      this.operation.panoZooming = false
      // 仅缩放fov，无平移操作
      this.dzoom = cam.fov / this.totalRatio - cam.fov
      /* 角度缩放最值 */
      // cam.fov = THREE.MathUtils.clamp(fov, 45, 65)
    }
    // godMode
    if (this.operation.godRotate) {
      this.operation.godRotate = false
      this.dlon = this.totalX * 0.4
      // this.dampingDeltaLon = this.totalX * 0.4
      // cam.lon += this.dampingDeltaLon
      if (cam.cameraType) {
        this.dlat = this.totalY * 0.2
        // this.dampingDeltaLat = this.totalY * 0.2
        // cam.lat += this.dampingDeltaLat
      }
    }
    if (this.operation.godZooming) {
      this.operation.godZooming = false
      if (cam.cameraType) {
        this.dzoom = cam.zoom / this.totalRatio - cam.zoom
        // cam.zoom /= this.totalRatio
      } else {
        this.dzoom = this.totalRatio * cam.camera.zoom - cam.camera.zoom
        // cam.camera.zoom *= this.totalRatio
        // cam.camera.zoom = cam.camera.zoom > 0.5 ? (cam.camera.zoom < 2.0 ? cam.camera.zoom : 2.0) : 0.5
        // cam.camera.updateProjectionMatrix()
      }
    }
    if (this.operation.godPanning) {
      this.operation.godPanning = false
      if (cam.cameraType) {
        this.dtranslate.x = this.totalMidX
        this.dtranslate.y = this.totalMidY
        // cam.panning(this.totalMidX, this.totalMidY)
      } else {
        this.dtranslate.x = this.totalMidX / 20
        this.dtranslate.y = this.totalMidY / 20
        // cam.panning((this.totalMidX) / 20, (this.totalMidY) / 20)
      }
    }

    if (this.shootMove) {
      // 获取最后一帧的变化值
      this.pdlon = this.dlon
      this.pdlat = this.dlat
      // 用列表储存this.listLength次更新的值，然后根据这几次决定松手时的pdlon、pdlat
      this.lonList[this.listPtr] = this.dlon
      this.latList[this.listPtr] = this.dlat
      this.listPtr = (++this.listPtr) % this.listLength // 循环填充列表
      for (let i = 0; i < this.listLength; ++i) {
        if (this.lonList[i] !== 0) { // 最近几个值里找最大的那个
          if (this.pdlon > 0 && this.lonList[i] > this.pdlon) {
            this.pdlon = this.lonList[i]
          } else if (this.pdlon < 0 && this.lonList[i] < this.pdlon) {
            this.pdlon = this.lonList[i]
          }
        }
        if (this.latList[i] !== 0) { // 最近几个值里找最大的那个
          if (this.pdlat > 0 && this.latList[i] > this.pdlat) {
            this.pdlat = this.latList[i]
          } else if (this.pdlat < 0 && this.latList[i] < this.pdlat) {
            this.pdlat = this.latList[i]
          }
        }
      }
      this.shootMove = false
    }

    // 修正为非点击事件
    this.activate = this.moveFlag || (!this.touchUp && this.touchDown)
  }

  /**
   * 清楚相机累加的更新数据
   */
  clearTransformation () {
    super.clearTransformation()

    this.touchDown = !this.touchUp && this.touchDown
    this.moveFlag = !this.touchUp && this.moveFlag
    this.touchUp = false

    this.totalX = 0
    this.totalY = 0
    this.totalRatio = 1
    this.totalMidX = 0
    this.totalMidY = 0
  }

  /**
   * 触屏事件处理。
   */
  handleTouchEvent () {
    if (this.touchUp) {
      if (!this.moveFlag) {
        if (this.state === this.enumState.free) {
          super.moveTo()
        } else {
          if (this.state === this.enumState.hotpoint) { // 处理热点交互
            if (this.currHotpoint.onClick) {
              this.currHotpoint.onClick()
            }
          }
        }
      }
    }
  }

  /**
   * 每帧调用的更新方法
   */
  update () {
    if (this.enable) {
      if (this.back2Model) {
        this.back2Model = false
        this.controller.toGod()
      }
    }
  }
}

export {
  MobileTouch
}
